import {
  Box,
  Divider,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { type RestoreAccount, Provider, AccountState } from '@repo/api-gw-sdk';
import { useState } from 'react';

import { BulletItem } from '@/app/(blank-layout-pages)/(authenticated)/onboarding/bulletItem';
import { extractErrorMessage } from '@/app/(blank-layout-pages)/(authenticated)/onboarding/errorHandling';
import { NumericBulletSection } from '@/app/(blank-layout-pages)/(authenticated)/onboarding/numericBulletSection';
import { AccountIndicator } from '@/components/accounts/accountIndicator';
import { TerraformBashToggle } from '@/components/accounts/terraformBashToggle';
import { StepErrorDialog } from '@/components/dialogs/stepErrorDialog';
import { CustomAccountDisplayName } from '@/components/form/customAccountDisplayName';
import { FormTextField } from '@/components/form/formTextField';
import { getCloudFieldConfig } from '@/components/form/validation/cloudFieldValidation';
import { Loader } from '@/components/layout/loading';
import { CopyableCode } from '@/components/shared/copyableCode';
import { StepContainer } from '@/components/wizard/StepContainer';
import type { StepProps } from '@/components/wizard/StepProps';
import { FEATURES_SUPPORT_VERSIONS } from '@/configs/featureConfig';
import { useDAL } from '@/contexts/useDAL';
import { useEnvironment } from '@/contexts/useEnvironment';
import { useUser } from '@/contexts/useUser';
import { useIsAccountFeatureSupported } from '@/utils/useIsAccountFeatureSupported';

import { getMongoAtlasRestoreProjectCode } from './mongoAtlasRestoreCode';

export interface MongoAtlasConnectRestoreProjectState {
  connectMethod: 'terraform' | 'bash';
  projectId: string;
  apiPublicKey: string;
  apiPrivateKey: string;
  restoreAccount?: RestoreAccount;
}

const projectIdConfig = getCloudFieldConfig(Provider.MongoAtlas, 'projectId');

export const ConnectMongoAtlasRestoreProject = (
  props: StepProps<MongoAtlasConnectRestoreProjectState>
) => {
  const [isDeploying, setIsDeploying] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [customName, setCustomName] = useState('');
  const user = useUser();
  const dal = useDAL();
  const { isDemo, isDev } = useEnvironment();
  const isAccountFeatureSupported = useIsAccountFeatureSupported();

  const { data: restoreAccountsBody } = dal.cloudAccounts.restore.useList();
  const availableRestoreAccounts = (restoreAccountsBody?.accounts ?? []).filter(
    (account) => {
      if (account.status !== AccountState.ACCOUNT_STATE_CONNECTED) return false;
      if (
        !account.restoreAccountAttributes.cloudProvider ||
        account.restoreAccountAttributes.cloudProvider !== Provider.Aws
      )
        return false;
      if (
        !isDemo &&
        !isDev &&
        FEATURES_SUPPORT_VERSIONS.MONGO_ATLAS_ONBOARDING &&
        !isAccountFeatureSupported(
          FEATURES_SUPPORT_VERSIONS.MONGO_ATLAS_ONBOARDING,
          account
        )
      )
        return false;
      return true;
    }
  );

  const restoreAccountId =
    props.wizardState.restoreAccount?.providerAccountId || '';

  const code = getMongoAtlasRestoreProjectCode(
    props.wizardState.projectId,
    user.eonAccount?.id ?? '',
    restoreAccountId,
    props.wizardState.connectMethod
  );

  const isMissingCodeFields = !props.wizardState.projectId || !restoreAccountId;

  const canGoNext = () => {
    const validProjectId = projectIdConfig.validation.validateInput(
      props.wizardState.projectId
    );
    const hasApiKeys =
      !!props.wizardState.apiPublicKey && !!props.wizardState.apiPrivateKey;
    const hasRestoreAccount = !!props.wizardState.restoreAccount;

    return !isDeploying && validProjectId && hasApiKeys && hasRestoreAccount;
  };

  const onNextClick = async () => {
    setError(null);
    setIsDeploying(true);

    try {
      await dal.cloudAccounts.restore.connect.mongoAtlas({
        name: customName || undefined,
        apiPublicKey: props.wizardState.apiPublicKey,
        apiPrivateKey: props.wizardState.apiPrivateKey,
        atlasProjectId: props.wizardState.projectId,
        awsRestoreAccountId: props.wizardState.restoreAccount!.id,
      });
      await props.finish();
    } catch (error: unknown) {
      const errorMessage = await extractErrorMessage(error);
      setError(new Error(errorMessage));
    } finally {
      setIsDeploying(false);
    }
  };

  const bulletTitle = `Provide your project ID and ${
    props.wizardState.connectMethod === 'terraform'
      ? 'apply the generated Terraform module'
      : 'run the generated Bash command'
  } in your environment.`;

  return (
    <>
      <StepContainer
        stepperIndex={0}
        hideStepper
        stepperLabels={props.stepperLabels}
        canGoNext={canGoNext}
        nextButtonDisabledTooltip={
          isDeploying
            ? 'Please wait for the deployment to complete'
            : 'Please fill all required fields'
        }
        nextButtonText='Connect'
        backButtonText='Cancel'
        onBackClick={props.back}
        sx={{ overflow: 'auto', paddingX: '0px', paddingTop: '24px' }}
        onNextClick={onNextClick}
      >
        <Stack divider={<Divider />} gap='24px'>
          <TerraformBashToggle
            text='Run the setup below on the target project.'
            connectMethod={props.wizardState.connectMethod}
            setConnectMethod={(connectMethod) => {
              props.setWizardState({ ...props.wizardState, connectMethod });
            }}
          />
          <Box
            paddingX='min(100px,6vw)'
            paddingTop='16px'
            width='100%'
            height='auto'
          >
            <Stack
              gap='40px'
              divider={<Divider sx={{ borderStyle: 'dashed' }} />}
            >
              <NumericBulletSection bulletNumber={1} title='Run the setup'>
                <BulletItem
                  title={bulletTitle}
                  leftContent={
                    <Stack
                      gap='24px'
                      divider={<Divider sx={{ borderStyle: 'dashed' }} />}
                    >
                      <Stack gap='24px'>
                        <Typography variant='subtitle1'>
                          AWS Restore Account
                        </Typography>
                        {!restoreAccountsBody ? (
                          <Loader />
                        ) : availableRestoreAccounts.length === 0 ? (
                          <Typography
                            variant='subtitle2'
                            color='text.secondary'
                          >
                            You must first add an AWS restore account before
                            connecting an Atlas restore project. Go to Settings
                            &gt; Restore Accounts &gt; AWS accounts to add one.
                          </Typography>
                        ) : (
                          <Select
                            size='small'
                            displayEmpty
                            value={props.wizardState.restoreAccount?.id ?? ''}
                            onChange={(e) => {
                              const account = availableRestoreAccounts.find(
                                (a) => a.id === e.target.value
                              );
                              props.setWizardState((state) => ({
                                ...state,
                                restoreAccount: account,
                              }));
                            }}
                            renderValue={(selected) => {
                              if (!selected) {
                                return (
                                  <Typography color='text.secondary'>
                                    Select a restore account
                                  </Typography>
                                );
                              }
                              const account = availableRestoreAccounts.find(
                                (a) => a.id === selected
                              );
                              if (!account) return null;
                              return (
                                <AccountIndicator
                                  account={account}
                                  copyable={false}
                                />
                              );
                            }}
                          >
                            {availableRestoreAccounts.map((account) => (
                              <MenuItem key={account.id} value={account.id}>
                                <AccountIndicator
                                  account={account}
                                  copyable={false}
                                />
                              </MenuItem>
                            ))}
                          </Select>
                        )}
                        <FormTextField
                          title='Project ID'
                          placeholder={projectIdConfig.placeholder}
                          validation={projectIdConfig.validation}
                          value={props.wizardState.projectId || ''}
                          onChange={(event) =>
                            props.setWizardState({
                              ...props.wizardState,
                              projectId: event.target.value,
                            })
                          }
                        />
                      </Stack>
                      <CustomAccountDisplayName
                        entityName='project'
                        customName={customName}
                        setCustomName={setCustomName}
                      />
                    </Stack>
                  }
                  rightContent={
                    <CopyableCode
                      title={
                        props.wizardState.connectMethod === 'terraform'
                          ? 'main.tf'
                          : 'mongo-atlas-eon-setup.sh'
                      }
                      code={code}
                      blur={isMissingCodeFields}
                    />
                  }
                />
              </NumericBulletSection>
              <NumericBulletSection
                bulletNumber={2}
                title='Complete configuration'
              >
                <BulletItem
                  title='After running the setup, enter the Eon API key from the output below.'
                  leftContent={
                    <Stack gap='24px'>
                      <TextField
                        placeholder='Eon API Public Key'
                        value={props.wizardState.apiPublicKey}
                        onChange={(e) =>
                          props.setWizardState({
                            ...props.wizardState,
                            apiPublicKey: e.target.value,
                          })
                        }
                        disabled={isDeploying}
                        fullWidth
                        size='small'
                      />
                      <TextField
                        placeholder='Eon API Private Key'
                        type='password'
                        value={props.wizardState.apiPrivateKey}
                        onChange={(e) =>
                          props.setWizardState({
                            ...props.wizardState,
                            apiPrivateKey: e.target.value,
                          })
                        }
                        disabled={isDeploying}
                        fullWidth
                        size='small'
                      />
                    </Stack>
                  }
                />
              </NumericBulletSection>
            </Stack>
          </Box>
        </Stack>
      </StepContainer>
      <StepErrorDialog
        isOpen={!!error}
        onClose={() => {
          setError(null);
          setIsDeploying(false);
        }}
        error={error}
      />
    </>
  );
};
